import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sun, Moon, Plus, Trash2, Flame, CalendarClock, Save, CheckSquare, Square } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function DailyOrganiser() {
  const [wake, setWake] = useState("07:00");
  const [sleep, setSleep] = useState("22:00");
  const [grid, setGrid] = useState({});
  const [tasks, setTasks] = useState([]);
  const [locked, setLocked] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [newType, setNewType] = useState("homework");
  const [newDue, setNewDue] = useState("");

  useEffect(() => {
    api.get("/organiser").then(r => {
      const d = r.data;
      setWake(d.wake_time || "07:00");
      setSleep(d.sleep_time || "22:00");
      setGrid(d.grid || {});
      setTasks(d.tasks || []);
      setLocked(d.locked_slots || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (patch = {}) => {
    const payload = { wake_time: wake, sleep_time: sleep, grid, tasks, ...patch };
    try { await api.put("/organiser", payload); } catch { toast.error("Could not save organiser"); }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const t = { id: `t_${Date.now()}`, title: newTask.trim(), type: newType, due: newDue || null,
                done: false, streak: 0 };
    const next = [...tasks, t];
    setTasks(next); setNewTask(""); setNewDue("");
    save({ tasks: next });
  };
  const toggleTask = (id) => {
    const next = tasks.map(t => t.id === id
      ? { ...t, done: !t.done, streak: !t.done ? (t.streak || 0) + 1 : Math.max(0, (t.streak || 0) - 1) }
      : t);
    setTasks(next); save({ tasks: next });
  };
  const delTask = (id) => { const next = tasks.filter(t => t.id !== id); setTasks(next); save({ tasks: next }); };

  const daysTo = (due) => {
    if (!due) return null;
    return Math.ceil((new Date(due).getTime() - Date.now()) / 86400000);
  };

  if (!loaded) return <div className="mono text-sm p-4">Loading organiser…</div>;

  return (
    <div className="space-y-5" data-testid="daily-organiser">
      {/* Wake / Sleep frame */}
      <div className="nb-card p-4 bg-[#FDE047] border-2 border-black">
        <div className="text-[11px] font-bold uppercase tracking-wide mb-2">Daily Frame</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-bold flex items-center gap-2"><Sun size={16} /> Wake
            <input type="time" className="nb-input ml-auto w-28" value={wake}
              onChange={e => { setWake(e.target.value); }} onBlur={() => save()} data-testid="organiser-wake" />
          </label>
          <label className="text-sm font-bold flex items-center gap-2"><Moon size={16} /> Sleep
            <input type="time" className="nb-input ml-auto w-28" value={sleep}
              onChange={e => { setSleep(e.target.value); }} onBlur={() => save()} data-testid="organiser-sleep" />
          </label>
        </div>
      </div>

      {/* Locked school slots (tenant students) */}
      {locked.length > 0 && (
        <div className="nb-card-sm p-3 bg-[#93C5FD] border-2 border-black text-xs">
          <b>{locked.length}</b> locked school timetable slots are synced and cannot be edited.
        </div>
      )}

      {/* Weekly grid */}
      <div className="nb-card p-4 bg-white border-2 border-black">
        <div className="text-[11px] font-bold uppercase tracking-wide mb-2">Weekly Self-Study Grid</div>
        <div className="space-y-2">
          {DAYS.map(day => (
            <div key={day} className="flex items-start gap-2">
              <div className="w-12 shrink-0 text-xs font-black pt-2">{day}</div>
              <textarea rows={1} className="nb-input flex-1 text-sm resize-y"
                placeholder="Plan your day…" value={grid[day] || ""}
                onChange={e => setGrid({ ...grid, [day]: e.target.value })}
                onBlur={() => save()} data-testid={`organiser-grid-${day}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Task checklist */}
      <div className="nb-card p-4 bg-[#86EFAC] border-2 border-black">
        <div className="text-[11px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1">
          <CheckSquare size={14} /> Tasks · Homework · Exam Countdowns
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <input className="nb-input flex-1 min-w-[120px] text-sm" placeholder="Add a task…"
            value={newTask} onChange={e => setNewTask(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTask()} data-testid="organiser-newtask" />
          <select className="nb-select text-sm w-28" value={newType} onChange={e => setNewType(e.target.value)} data-testid="organiser-tasktype">
            <option value="homework">Homework</option>
            <option value="habit">Habit</option>
            <option value="exam">Exam</option>
          </select>
          <input type="date" className="nb-input text-sm w-36" value={newDue} onChange={e => setNewDue(e.target.value)} data-testid="organiser-taskdue" />
          <button className="nb-btn" onClick={addTask} data-testid="organiser-addtask"><Plus size={14} /></button>
        </div>
        <div className="space-y-2">
          {tasks.map(t => {
            const d = daysTo(t.due);
            return (
              <div key={t.id} className="flex items-center gap-2 bg-white border-2 border-black rounded-lg px-3 py-2" data-testid={`organiser-task-${t.id}`}>
                <button onClick={() => toggleTask(t.id)} data-testid={`organiser-task-toggle-${t.id}`}>
                  {t.done ? <CheckSquare size={18} /> : <Square size={18} />}
                </button>
                <span className={`flex-1 text-sm ${t.done ? "line-through opacity-50" : ""}`}>{t.title}</span>
                {t.type === "habit" && (t.streak || 0) > 0 &&
                  <span className="nb-badge bg-[#FCA5A5] text-[10px]"><Flame size={11} /> {t.streak}</span>}
                {t.type === "exam" && d != null &&
                  <span className="nb-badge bg-[#FDE047] text-[10px]"><CalendarClock size={11} /> {d}d</span>}
                <button onClick={() => delTask(t.id)} data-testid={`organiser-task-del-${t.id}`}><Trash2 size={15} className="text-red-600" /></button>
              </div>
            );
          })}
          {!tasks.length && <div className="text-xs text-center opacity-60 py-2">No tasks yet — add homework, habits or exam dates.</div>}
        </div>
      </div>

      <button className="nb-btn w-full justify-center" onClick={() => { save(); toast.success("Organiser saved"); }} data-testid="organiser-save">
        <Save size={16} /> Save Organiser
      </button>
    </div>
  );
}
